import React from "react"
import AsyncSelectBase from "react-select/async"
import CreatableSelectBase from "react-select/creatable"
import SelectBase, { components } from "react-select"
import "./Select.scss"
import styles from "./Select.module.scss"
import Spinner from "../Spinner/Spinner"
import { OptionType } from "../AsyncSearchableInput/AsyncSearchableInput"
import { AsyncProps } from "react-select/src/Async"
import { Props as SelectProps } from "react-select/src/Select"
import cn from "classnames"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCaretDown, faTimes } from "@fortawesome/pro-light-svg-icons"
import { testId } from "../../utility/tests/testId/common"
import { CreatableSelectProps, SelectCustomProps } from "./Select.types"

const LoadingIndicator = () => <Spinner className="custom-select__spinner" size={15} />
type SelectContainerProps = React.ComponentProps<typeof components.SelectContainer>
type ControlProps = React.ComponentProps<typeof components.Control>
type SelectComponents = NonNullable<SelectCustomProps["components"]>

const selectPropsTestId = (selectProps: ControlProps["selectProps"]) =>
    (selectProps as SelectCustomProps).testId

// При наличии testId селект рендерит два data-testid атрибута:
//   - testId              — на внешнем SelectContainer (для выбора компонента целиком)
//   - testId + "-control" — на внутреннем Control (для взаимодействий: клик, фокус)
const TestIdSelectContainer: React.FC<SelectContainerProps> = props => {
    const selectTestId = selectPropsTestId(props.selectProps)
    const innerProps = (
        selectTestId ? { ...props.innerProps, "data-testid": selectTestId } : props.innerProps
    ) as SelectContainerProps["innerProps"]

    return <components.SelectContainer {...props} innerProps={innerProps} />
}

const TestIdControl: React.FC<ControlProps> = props => {
    const selectTestId = selectPropsTestId(props.selectProps)
    const innerProps = (
        selectTestId ? { ...props.innerProps, "data-testid": `${selectTestId}-control` } : props.innerProps
    ) as ControlProps["innerProps"]

    return <components.Control {...props} innerProps={innerProps} />
}

const ClearIndicator = (props: React.ComponentPropsWithRef<typeof components.ClearIndicator>) => {
    return (
        <div className={styles.indicator}>
            <FontAwesomeIcon
                icon={faTimes}
                className={styles.indicator__icon}
                onClick={props.clearValue}
                data-testid={testId.indicatorIconClearValue}
            />
        </div>
    )
}
const DropdownIndicator = () => (
    <div className={styles.indicator}>
        <FontAwesomeIcon icon={faCaretDown} className={styles.indicator__icon} />
    </div>
)

const getSelectContainer = (customComponents?: SelectComponents) =>
    customComponents?.SelectContainer ?? TestIdSelectContainer

const getSelectClassName = (className?: string, invalid?: boolean) => cn(className, invalid && "custom-select_invalid")

export const AsyncSelect: React.FC<SelectProps<OptionType, false> & AsyncProps<OptionType> & SelectCustomProps> =
    props => {
        const { components: customComponents, invalid, ...selectProps } = props

        return (
            <AsyncSelectBase
                {...selectProps}
                menuShouldScrollIntoView={false}
                classNamePrefix="custom-select"
                components={{
                    LoadingIndicator,
                    ClearIndicator,
                    DropdownIndicator,
                    ...customComponents,
                    Control: customComponents?.Control ?? TestIdControl,
                    SelectContainer: getSelectContainer(customComponents)
                }}
                className={getSelectClassName(props.className, invalid)}
            />
        )
    }

export const Select: React.FC<SelectCustomProps> = props => {
    const { components: customComponents, invalid, ...selectProps } = props

    return (
        <SelectBase
            {...selectProps}
            menuShouldScrollIntoView={false}
            classNamePrefix="custom-select"
            className={getSelectClassName(props.className, invalid)}
            components={{
                DropdownIndicator,
                ClearIndicator,
                ...customComponents,
                Control: customComponents?.Control ?? TestIdControl,
                SelectContainer: getSelectContainer(customComponents)
            }}
        />
    )
}

export const CreatableSelect: React.FC<CreatableSelectProps> = props => {
    const { components: customComponents, invalid, ...selectProps } = props

    return (
        <CreatableSelectBase
            {...selectProps}
            classNamePrefix="custom-select"
            className={getSelectClassName(props.className, invalid)}
            components={{
                DropdownIndicator: props.hideDropdownIndicator ? null : DropdownIndicator,
                ClearIndicator,
                LoadingIndicator,
                ...customComponents,
                Control: customComponents?.Control ?? TestIdControl,
                SelectContainer: getSelectContainer(customComponents)
            }}
        />
    )
}
